/**
 * Predictive Search
 *
 * Listens on elements with [data-predictive-search-input], fetches results
 * from the Shopify predictive search endpoint, and renders them into the
 * container with [data-predictive-search-results].
 *
 * Features:
 * - 300 ms debounce
 * - AbortController for stale requests
 * - Simple Map cache for repeated queries
 * - Keyboard navigation (ArrowUp / ArrowDown / Enter / Escape)
 * - Click-outside to close
 * - Loading state
 * - Minimum 2 characters before searching
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------
  const DEBOUNCE_MS = 300;
  const MIN_CHARS = 2;
  const RESOURCE_TYPES = 'product,collection,page,article';
  const RESOURCE_LIMIT = 4;
  const SECTION_ID = 'predictive-search';

  // ---------------------------------------------------------------
  // State
  // ---------------------------------------------------------------
  const cache = new Map();
  let activeController = null;
  let debounceTimer = null;
  let activeInput = null;
  let activeResults = null;
  let focusedIndex = -1;

  // ---------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------

  function getSearchUrl() {
    return window.routes?.predictive_search_url || '/search/suggest';
  }

  function buildFetchUrl(query) {
    const base = getSearchUrl();
    const params = new URLSearchParams({
      q: query,
      'resources[type]': RESOURCE_TYPES,
      'resources[limit]': RESOURCE_LIMIT,
      section_id: SECTION_ID,
    });
    return `${base}?${params.toString()}`;
  }

  /**
   * Return all navigable items (links) inside the results container.
   */
  function getNavigableItems() {
    if (!activeResults) return [];
    return Array.from(activeResults.querySelectorAll('[data-predictive-search-item] a'));
  }

  // ---------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------

  function showLoading() {
    if (!activeResults) return;
    activeResults.innerHTML =
      '<div class="predictive-search__loading"><span class="predictive-search__spinner"></span></div>';
    activeResults.classList.add('is-open');
    activeResults.classList.add('is-loading');
  }

  function hideResults() {
    if (!activeResults) return;
    activeResults.classList.remove('is-open');
    activeResults.classList.remove('is-loading');
    focusedIndex = -1;
    clearFocus();
  }

  function clearFocus() {
    if (!activeResults) return;
    activeResults.querySelectorAll('[data-predictive-search-item]').forEach(function (item) {
      item.classList.remove('is-focused');
      item.setAttribute('aria-selected', 'false');
    });
  }

  function setFocus(index) {
    const items = getNavigableItems();
    if (items.length === 0) return;

    // Wrap index
    if (index < 0) index = items.length - 1;
    if (index >= items.length) index = 0;

    focusedIndex = index;

    clearFocus();

    const link = items[focusedIndex];
    const listItem = link.closest('[data-predictive-search-item]');
    if (listItem) {
      listItem.classList.add('is-focused');
      listItem.setAttribute('aria-selected', 'true');
    }

    // Scroll into view if needed
    link.scrollIntoView({ block: 'nearest' });
  }

  // ---------------------------------------------------------------
  // Fetch & render
  // ---------------------------------------------------------------

  function fetchResults(query) {
    // Cancel any in-flight request
    if (activeController) {
      activeController.abort();
    }
    activeController = new AbortController();

    const cacheKey = query.toLowerCase().trim();

    // Serve from cache if available
    if (cache.has(cacheKey)) {
      renderResults(cache.get(cacheKey));
      return;
    }

    showLoading();

    const url = buildFetchUrl(query);

    fetch(url, { signal: activeController.signal })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Search request failed: ' + response.status);
        }
        return response.text();
      })
      .then(function (html) {
        // Parse the returned section HTML
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var sectionEl = doc.querySelector('#shopify-section-' + SECTION_ID);
        var resultsHTML = sectionEl ? sectionEl.innerHTML : '';

        // Cache
        cache.set(cacheKey, resultsHTML);

        renderResults(resultsHTML);
      })
      .catch(function (error) {
        if (error.name === 'AbortError') return; // expected
        console.error('Predictive search error:', error);
        hideResults();
      });
  }

  function renderResults(html) {
    if (!activeResults) return;

    activeResults.innerHTML = html;
    activeResults.classList.remove('is-loading');
    focusedIndex = -1;

    if (html.trim().length > 0) {
      activeResults.classList.add('is-open');
    } else {
      hideResults();
    }
  }

  // ---------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------

  function onInput(event) {
    var input = event.target;
    var query = input.value.trim();

    // Find the associated results container
    activeInput = input;
    activeResults = findResultsContainer(input);

    clearTimeout(debounceTimer);

    if (query.length < MIN_CHARS) {
      hideResults();
      return;
    }

    debounceTimer = setTimeout(function () {
      fetchResults(query);
    }, DEBOUNCE_MS);
  }

  function onKeydown(event) {
    if (!activeResults || !activeResults.classList.contains('is-open')) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocus(focusedIndex + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        setFocus(focusedIndex - 1);
        break;

      case 'Enter':
        if (focusedIndex >= 0) {
          event.preventDefault();
          var items = getNavigableItems();
          if (items[focusedIndex]) {
            items[focusedIndex].click();
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        hideResults();
        if (activeInput) activeInput.blur();
        break;
    }
  }

  function onFocusIn(event) {
    var input = event.target;
    if (!input.hasAttribute('data-predictive-search-input')) return;

    activeInput = input;
    activeResults = findResultsContainer(input);

    var query = input.value.trim();
    if (query.length >= MIN_CHARS) {
      var cacheKey = query.toLowerCase().trim();
      if (cache.has(cacheKey)) {
        renderResults(cache.get(cacheKey));
      }
    }
  }

  function onClickOutside(event) {
    if (!activeResults || !activeResults.classList.contains('is-open')) return;

    // If the click is inside the input or results container, ignore
    if (activeInput && activeInput.contains(event.target)) return;
    if (activeResults && activeResults.contains(event.target)) return;

    // Also check if the click is on the search wrapper itself
    var wrapper = activeInput ? activeInput.closest('.header__search') : null;
    if (wrapper && wrapper.contains(event.target)) return;

    hideResults();
  }

  // ---------------------------------------------------------------
  // Utility
  // ---------------------------------------------------------------

  /**
   * Find the results container associated with the given input.
   * Looks for [data-predictive-search-results] as a sibling or
   * within the same parent container.
   */
  function findResultsContainer(input) {
    // First check siblings
    var parent = input.parentElement;
    if (parent) {
      var sibling = parent.querySelector('[data-predictive-search-results]');
      if (sibling) return sibling;
    }

    // Walk up to find within a larger wrapper
    var wrapper = input.closest('[data-predictive-search-wrapper]');
    if (wrapper) {
      return wrapper.querySelector('[data-predictive-search-results]');
    }

    // Fallback: any results container on the page
    return document.querySelector('[data-predictive-search-results]');
  }

  // ---------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------

  function init() {
    // Delegate input events
    document.addEventListener('input', function (event) {
      if (event.target.hasAttribute('data-predictive-search-input')) {
        onInput(event);
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', function (event) {
      if (
        activeInput &&
        document.activeElement === activeInput &&
        activeResults &&
        activeResults.classList.contains('is-open')
      ) {
        onKeydown(event);
      } else if (event.key === 'Escape' && activeResults) {
        hideResults();
      }
    });

    // Focus in (re-open cached results)
    document.addEventListener('focusin', onFocusIn);

    // Click outside to close
    document.addEventListener('click', onClickOutside);
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
